import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, BadgeCheck, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Customer-facing Production Priority capture (Phase 3).
 *
 * Lets customers self-declare during Review Order:
 *  • Rush — bumps production priority server-side via is_rush=true.
 *    Customers see a clear note that surcharges may apply (admin review).
 *  • Reseller verification — "I'm a reseller". This is a REQUEST, not
 *    a tier assignment — admins still set the actual reseller_tier
 *    (silver/gold/platinum) via the cockpit's quick-attrs editor after
 *    they verify the customer's reseller account.
 *
 * Auto-saves on every toggle. Optimistic UI with toast confirmation;
 * reverts the local state on backend failure.
 */
export const ProductionPriorityCard = ({ orderId, order }) => {
  const [isRush, setIsRush] = useState(Boolean(order?.is_rush));
  const [resellerRequested, setResellerRequested] = useState(
    Boolean(order?.reseller_verification_requested),
  );
  const [busyField, setBusyField] = useState(null);

  // Re-sync local state when the order doc reloads (e.g. after a parent
  // refetch). Without this the customer's toggle could be stomped on
  // by a stale optimistic value during background reloads.
  useEffect(() => {
    setIsRush(Boolean(order?.is_rush));
    setResellerRequested(Boolean(order?.reseller_verification_requested));
  }, [order?.is_rush, order?.reseller_verification_requested]);

  const persist = useCallback(
    async (field, value, prev, setter) => {
      if (!orderId) return;
      setBusyField(field);
      setter(value); // optimistic
      try {
        await axios.post(
          `${API_URL}/api/enhanced-orders/${orderId}/production-prefs`,
          { [field]: value },
        );
        if (field === 'is_rush') {
          toast.success(value ? 'Rush production requested' : 'Rush flag removed', {
            description: value
              ? "We'll prioritize your order — final pricing confirmed by our team."
              : 'Standard production timeline restored.',
          });
        } else {
          toast.success(
            value
              ? 'Reseller verification requested'
              : 'Reseller verification request cleared',
            {
              description: value
                ? "We'll review your reseller account before fulfillment."
                : null,
            },
          );
        }
      } catch (err) {
        setter(prev); // rollback
        const detail = err?.response?.data?.detail || 'Could not save preference.';
        toast.error(detail);
      } finally {
        setBusyField(null);
      }
    },
    [orderId],
  );

  return (
    <Card
      data-testid="production-priority-card"
      className="border-2 border-[#6FBF73]/30 bg-gradient-to-br from-white via-[#F4FBF6] to-white"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold text-[#14281D] flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#4A7C59]" />
          Production Priority
        </CardTitle>
        <p className="text-xs text-[#1F3D2C]/70 mt-1">
          Optional preferences — our team reviews and confirms each before fulfillment.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Rush toggle */}
        <label
          htmlFor="prod-pref-rush"
          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
            isRush
              ? 'border-red-400 bg-red-50/60'
              : 'border-gray-200 bg-white hover:border-[#6FBF73]/40'
          }`}
        >
          <input
            id="prod-pref-rush"
            type="checkbox"
            data-testid="prod-pref-rush-checkbox"
            checked={isRush}
            disabled={busyField === 'is_rush'}
            onChange={(e) =>
              persist('is_rush', e.target.checked, isRush, setIsRush)
            }
            className="mt-1 w-4 h-4 accent-red-500"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#14281D]">
                Mark this order as <span className="text-red-600">Rush</span>
              </span>
              {busyField === 'is_rush' && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-xs text-[#1F3D2C]/70 mt-1">
              Bump this job to the top of our production queue. A rush surcharge
              may apply depending on your timeline — we&apos;ll confirm before
              charging.
            </p>
          </div>
        </label>

        {/* Reseller verification toggle */}
        <label
          htmlFor="prod-pref-reseller"
          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
            resellerRequested
              ? 'border-amber-400 bg-amber-50/60'
              : 'border-gray-200 bg-white hover:border-[#6FBF73]/40'
          }`}
        >
          <input
            id="prod-pref-reseller"
            type="checkbox"
            data-testid="prod-pref-reseller-checkbox"
            checked={resellerRequested}
            disabled={busyField === 'reseller_verification_requested'}
            onChange={(e) =>
              persist(
                'reseller_verification_requested',
                e.target.checked,
                resellerRequested,
                setResellerRequested,
              )
            }
            className="mt-1 w-4 h-4 accent-amber-500"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#14281D] flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4 text-amber-600" />
                I&apos;m a reseller — please verify my account
              </span>
              {busyField === 'reseller_verification_requested' && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-xs text-[#1F3D2C]/70 mt-1">
              Resellers may be eligible for tier benefits (Silver / Gold /
              Platinum). Our team will reach out to verify your account before
              applying any reseller pricing.
            </p>
          </div>
        </label>
      </CardContent>
    </Card>
  );
};

export default ProductionPriorityCard;
